import { useMemo } from "react";

import { JobRead } from "@nens/rana/api-v1";

import { useJobListQuery } from "@/queries/query-job";

import { jobIsRunning } from "../../../utils/job-is-running";

export const calculateHistoricalEstimate = (jobs: JobRead[]): number | null => {
  const completedJobs = jobs.filter(
    (j): j is typeof j & { startedAt: Date; finishedAt: Date } =>
      j.state.type === "completed" && !!j.startedAt && !!j.finishedAt
  );

  if (!completedJobs.length) return null;

  return (
    completedJobs.reduce((sum, j) => sum + j.finishedAt.getTime() - j.startedAt.getTime(), 0) /
    completedJobs.length
  );
};

export const calculateProgressEstimate = (
  startedAt: Date,
  progressUpdatedAt: Date,
  progress: number | undefined
): number | null => {
  const elapsedMs = progressUpdatedAt.getTime() - startedAt.getTime();
  return progress ? elapsedMs / progress : null;
};

export const combineEstimates = (
  progressMs: number | null,
  historicalMs: number | null
): number | null => {
  // If one of the estimates is missing, use the other one
  if (progressMs === null) return historicalMs;
  if (historicalMs === null) return progressMs;

  // Average both estimates when both are available
  return (progressMs + historicalMs) / 2;
};

export const useEstimatedTime = (job: JobRead | undefined) => {
  const jobQuery = useJobListQuery({
    processId: job?.process?.id,
    enabled: !!job && !!job.process && jobIsRunning(job.state.type),
    disableRefetching: true,
  });

  return useMemo(() => {
    if (!job) return null;

    const historicalMs = calculateHistoricalEstimate(jobQuery.data?.items ?? []);

    const { progressUpdatedAt, progress } = job.state;
    const { startedAt } = job;

    // If job hasn't started, we can only use historical data
    if (!startedAt || !progressUpdatedAt) return historicalMs;

    const progressMs = calculateProgressEstimate(startedAt, progressUpdatedAt, progress);

    return combineEstimates(progressMs, historicalMs);
  }, [job, jobQuery.data?.items]);
};
